import Component from '../inherited/Component';
import { create } from 'evx';
import Helpers from '../lib/Helpers';
import KeenSlider from 'keen-slider';

export default class KeenSliderComp extends Component {
  constructor(elem, theme, options, ctx) {
    options = {
      ...{
        dragSpeed: 1,
        autoplaySpeed: 5000,
        duration: 500,
        arrowSvg: `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="40" height="40" rx="20"/>
    <path d="m24.28 20.53-7.5 7.5a.752.752 0 0 1-1.223-.243.75.75 0 0 1 .162-.818L22.69 20l-6.97-6.968a.75.75 0 0 1 1.062-1.062l7.5 7.5a.75.75 0 0 1 0 1.062z" fill="currentColor"/>
    </svg>`
      },
      ...options
    };
    let optionsCopy = { ...options };
    super(elem, theme, options, ctx);
    this._slider = null;
    this._optionsCopy = optionsCopy;
    this._autoplayInterval = null;
    this.shouldAutoplay = false;
    this._localCtx = create();
    this._isMounted = false;
    this._addedListener = false;
    super.reinitOnResize(optionsCopy);
  }

  mount() {
    if (this._options.mount === false) {
      return false;
    }

    if (this._options.enableWhen && !this._addedListener) {
      window.addEventListener(
        'resize',
        (e) => {
          if (
            matchMedia(`only screen and ${this._options.enableWhen}`).matches &&
            !this._isMounted
          ) {
            this.mount();
          } else if (
            !matchMedia(`only screen and ${this._options.enableWhen}`).matches &&
            this._isMounted
          ) {
            this.unmount();
            this._isMounted = false;
          }
        },
        { passive: true }
      );
      this._addedListener = true;
    }

    if (
      this._options.enableWhen &&
      !matchMedia(`only screen and ${this._options.enableWhen}`).matches
    ) {
      return false;
    }

    this._slidesPerView =
      typeof this._options.slides === 'object'
        ? this._options.slides.perView || this._options.slides.number
        : this._options.slides;

    this._elem.classList.add('keen-slider');
    for (let elem of this._elem.children) {
      if (elem.hasAttribute('data-keen-dots') || elem.hasAttribute('data-keen-nav')) continue;
      elem.classList.add('keen-slider__slide');
      if (this._options.fade) {
        elem.classList.add('fader-slide');
      }
    }
    this._slides = this._elem.querySelectorAll('.keen-slider__slide');

    // AS NAV FOR
    if (this._options.asNavFor) {
      this._ctx.on('radiant--mounted', (e) => {
        this._parentSlider = this._theme.getComponent(this._options.asNavFor);
        this._parentSlider = this._parentSlider.component;

        this._parentSlider._localCtx.on('slide-change', (state, data) => {
          const next = data.slider.track.details.rel || 0;
          this._slider.moveToIdx(Math.min(this._slider.track.details.maxIdx, next));
        });
      });
    }

    if (this._options.arrows) this._createArrows();

    if (this._slides.length === this._slidesPerView) {
      this._options.loop = false;
      this._options.controls = false;
    }

    if (!this._prevArrow) this._prevArrow = this._elem.querySelector('[data-keen-prev]');
    if (!this._nextArrow) this._nextArrow = this._elem.querySelector('[data-keen-next]');

    this._options.animationStarted = (slider) => {
      this._localCtx.emit('animation-started', null, { slider: slider });
    };

    this._options.slideChanged = (slider) => {
      this.updateClasses(slider);
      this.updateFadeHeight(slider);
      this._localCtx.emit('slide-change', null, { slider: slider });
      this.adaptiveHeight(slider);
      if (this._isMounted) this.scrollRevealFix(slider);

      this._pauseVideos();
      if (this._options.playVideos) this._playVideos(slider);
      if (this._options.setSlideCount) this._setSlideCount(slider);

      this._doSlideCSSVars(slider);
      this._doSlideClasses(slider);

      if (this._options.asNavFor && this._parentSlider) {
        let parentSlider = this._parentSlider._slider;
        console.log(this._slider);
        const next = this._slider.track.details.rel || 0;
        parentSlider.moveToIdx(Math.min(parentSlider.track.details.maxIdx, next));
      }
    };

    this._options.created = (slider) => {
      this._elem.classList.add('keen-slider--ready');
      this._dotsContainer = this._options.dotsContainer
        ? document.querySelector(this._options.dotsContainer) || this._elem
        : this._elem;
      if (this._options.dots && this._slides.length > 1) this._createDots(slider);

      this._dots = this._dotsContainer.querySelectorAll('[data-dot]');

      for (let dot of this._dots) {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          this.goToSlide(Number(dot.dataset.dot), false);

          if (this._parentSlider) {
            this._parentSlider.goToSlide(Number(dot.dataset.dot));
          }
        });
      }

      try {
        for (let [index, slide] of slider.slides.entries()) {
          let position = slider.track.details.slides[index].abs;
          slide.style.setProperty('--reveal-index', position + 1);
        }
      } catch (e) {
        console.error(e);
      }

      this.updateClasses(slider);
      this.updateFadeHeight(slider);
      this.adaptiveHeight(slider);
      this._doSlideCSSVars(slider);
      this._doSlideClasses(slider);

      if (this._options.autoplay) this._setupAutoplay(slider);
      if (this._options.autoplayWhenVisibleOnly) this._doAutoplayOnScroll();
      if (this._options.alignArrowsTo) this.alignArrows();
      if (this._options.arrowKeyNavigation) this._addArrowKeyEvents();
      if (this._options.playVideos) this._playVideos(slider);
      if (this._options.setSlideCount) this._setSlideCount(slider);
      if (this._options.focusOnSelect) this.focusOnSelect(slider);

      window.addEventListener(
        'resize',
        (e) => {
          this.updateFadeHeight();
          this.adaptiveHeight();

          if (this._options.alignArrowsTo) {
            Helpers.nextFrame(() => {
              this.alignArrows();
            });
          }
        },
        { passive: true }
      );
    };

    this._options.dragStarted = () => {
      this.shouldAutoplay = false;
    };

    this._options.dragEnded = () => {
      this.shouldAutoplay = true;
    };

    if (this._options.fade) {
      this._options.detailsChanged = (slider) => this.fader(slider);
      this._options.renderMode = 'custom';
    }

    this._slider = new KeenSlider(this._elem, this._options);

    if (this._prevArrow) this._prevArrow.addEventListener('click', (e) => this.goToPrev(e));
    if (this._nextArrow) this._nextArrow.addEventListener('click', (e) => this.goToNext(e));

    this._isMounted = true;

    if (this._options.triggerOnMount) {
      this._ctx.emit(this._options.triggerOnMount);
    }
  }

  focusOnSelect(slider = this._slider) {
    for (let [index, slide] of this._slides.entries()) {
      slide.addEventListener('click', (e) => {
        this.goToSlide(index);
      });
    }
  }

  _setSlideCount(slider = this._slider) {
    let slideCountParentElem = document.querySelector(this._options.setSlideCount);

    if (slideCountParentElem) {
      let totalElem = slideCountParentElem.querySelector('[data-slide-total]');
      let currentElem = slideCountParentElem.querySelector('[data-slide-current]');

      if (totalElem && currentElem) {
        totalElem.innerHTML = Math.round(slider.track.details.slidesLength);
        currentElem.innerHTML = Math.round(slider.track.details.position + 1);
      }
    }
  }

  unmount() {
    if (this._slider) {
      this._slider.destroy();
    }
    if (this._navContainer && this._navContainer.parentNode) {
      this._navContainer.parentNode.removeChild(this._navContainer);
    }
    let dots = this._elem.querySelector('[data-keen-dots]');
    if (dots) {
      dots.parentNode.removeChild(dots);
    }
    this._elem.classList.remove('keen-slider');
    for (let elem of this._elem.children) {
      elem.classList.remove('keen-slider__slide');
      elem.classList.remove('fader-slide');
      elem.removeAttribute('style');
    }
    this._elem.style.height = '';
  }

  _setupAutoplay(slider) {
    if (!this._options.autoplay) {
      clearTimeout(this.autoplayTimeout);
      return false;
    }

    this.shouldAutoplay = true;
    this.shouldAutoplayVisible = true;

    if (this._options.pauseAutoplayOnHover) {
      this._elem.addEventListener('mouseover', (e) => {
        this.shouldAutoplay = false;
        clearTimeout(this.autoplayTimeout);
      });

      this._elem.addEventListener('mouseout', (e) => {
        this.shouldAutoplay = true;
        this.autoplay();
      });
    }

    this.autoplay();

    slider.on('dragStarted', () => {
      if (this.autoplayTimeout) clearTimeout(this.autoplayTimeout);
    });

    slider.on('animationEnded', () => {
      this.autoplay();
    });

    slider.on('updated', () => {
      this.autoplay();
    });
  }

  autoplay() {
    clearTimeout(this.autoplayTimeout);
    if (!this.shouldAutoplay || !this.shouldAutoplayVisible) return;

    this.autoplayTimeout = setTimeout(() => {
      if (this._slider) {
        if (this.isAtEnd() && !this._options.loop) {
          this.goToSlide(0);
        } else {
          this._slider.next();
        }
      }
    }, this._options.autoplaySpeed);
  }

  _doAutoplayOnScroll() {
    if (!this._options.autoplay) return false;

    let visibleCheck = () => {
      if (Helpers.isInViewport(this._elem) && this.shouldAutoplayVisible == false) {
        this.shouldAutoplayVisible = true;
        this.autoplay();
      } else {
        this.shouldAutoplayVisible = false;
        if (this.autoplayTimeout) clearTimeout(this.autoplayTimeout);
      }
    };

    window.addEventListener(
      'scroll',
      (e) => {
        visibleCheck();
      },
      { passive: true }
    );
    visibleCheck();
  }

  _doSlideCSSVars(slider = this._slider) {
    let slide = this.getActiveSlideElem(slider);

    if (slide && slide.dataset.slideCssVars) {
      let varArr = JSON.parse(slide.dataset.slideCssVars);

      for (let cssVar of varArr) {
        this._elem.style.setProperty(cssVar.name, cssVar.value);
      }
    }
  }

  _doSlideClasses(slider = this._slider) {
    let slide = this.getActiveSlideElem(slider);

    if (slide && slide.dataset.slideClasses) {
      let varArr = JSON.parse(slide.dataset.slideClasses);

      for (let cssVar of varArr) {
        this._elem.setAttribute(cssVar.name, cssVar.value);
      }
    }
  }

  _addArrowKeyEvents() {
    document.addEventListener('keydown', (e) => {
      let ev = e || window.event;
      if (ev.keyCode === 37) {
        // left arrow
        this._slider.prev();
      } else if (ev.keyCode === 39) {
        // right arrow
        this._slider.next();
      }
    });
  }

  _createArrows() {
    var e = document.createElement('ul');
    e.classList.add('keen-nav');
    e.setAttribute('data-keen-nav', true);
    e.innerHTML = `
        <li>
          <button data-keen-prev class="keen-arrow-prev keen-arrow" aria-label="Previous Slide">
            <span class="keen-arrow-inner">
              ${this._options.arrowSvg || ''}
            </span>
          </button>
        </li>
        <li>
          <button data-keen-next class="keen-arrow-next keen-arrow" aria-label="Next Slide">
            <span class="keen-arrow-inner">
              ${this._options.arrowSvg || ''}
            </span>
          </button>
        </li>
    `;
    this._navContainer = e;
    this._prevArrow = this._navContainer.querySelector('[data-keen-prev]');
    this._nextArrow = this._navContainer.querySelector('[data-keen-next]');

    if (this._options.appendArrows) {
      let toAppend = document.querySelector(this._options.appendArrows);
      if (toAppend) toAppend.appendChild(e);
    } else {
      this._elem.appendChild(e);
    }
  }

  _createDots(slider) {
    var e = document.createElement('ul');
    e.classList.add('keen-dots');
    e.setAttribute('data-keen-dots', true);

    let dotsToMake = slider.track.details.slidesLength;

    for (let i = 0; i < dotsToMake; i++) {
      e.innerHTML += `
        <li>
          <button aria-label='Slider Dot' data-dot='${i}'></button>
        </li>
      `;
    }

    this._dotsContainer.classList.add('keen--dotted');
    this._dotsContainer.appendChild(e);
  }

  _playVideos(slider = this._slider) {
    this._pauseVideos();

    Helpers.nextFrame(() => {
      let active = this.getActiveSlideElem();
      let activeVideo = active.querySelector('video');

      if (activeVideo) {
        if (activeVideo.hasAttribute('[data-lazy]') && !activeVideo.classList.contains('loaded')) {
          activeVideo.addEventListener('radiant--loaded', (e) => {
            console.log('loaded Event');
            activeVideo.play();
          });
        } else {
          activeVideo.play();
        }
      }
    });
  }

  _pauseVideos() {
    let videos = this._elem.querySelectorAll('video');
    for (let video of videos) {
      video.pause();
    }
  }

  fader(slider = this._slider) {
    for (let [index, slide] of slider.slides.entries()) {
      slide.style.opacity = slider.track.details.slides[index].portion;
    }
  }

  goToSlide(index, dontAnimate = false) {
    if (!this._slider) return;

    let duration = dontAnimate ? 0 : this._options.duration;

    let nearest = false;

    if (this._options.loop && this._slider.track.details.slides.length > 2) {
      nearest = true;
    }
    this._slider.moveToIdx(index, nearest, duration);
  }

  goToPrev(e) {
    e.preventDefault();
    this._slider.prev();
  }

  goToNext(e) {
    e.preventDefault();
    this._slider.next();
  }

  scrollRevealFix() {
    for (let slide of this._slides) {
      if (slide.hasAttribute('data-sr-id') || slide.hasAttribute('data-reveal')) {
        slide.style.removeProperty('opacity');
        slide.style.removeProperty('transition');
        slide.style.removeProperty('visibility');
        slide.removeAttribute('data-sr-id');
        slide.removeAttribute('data-reveal');
      }
    }
  }

  getActiveSlideElem(slider = this._slider) {
    let slideIndex = slider.track.details.rel;

    for (let [index, slide] of this._slides.entries()) {
      if (index === slideIndex) {
        return slide;
      }
    }

    return false;
  }

  adaptiveHeight(slider = this._slider) {
    if (!this._options.adaptiveHeight || !slider) return false;

    this._elem.classList.add('keen--adaptive-height');

    Helpers.nextFrame(() => {
      let slide = slider.track.details.rel;

      let height = 0;

      height += parseFloat(getComputedStyle(this._elem).paddingBottom);
      height += parseFloat(getComputedStyle(this._elem).paddingTop);

      let activeSlide = this.getActiveSlideElem(slider);

      if (!activeSlide) return;

      height += activeSlide.clientHeight;
      this._elem.style.height = `${height}px`;
    });
  }

  updateFadeHeight(slider = this._slider) {
    if (!this._options.fade || !slider) return false;

    let slide = slider.track.details.rel;

    let height = 0;

    height += parseFloat(getComputedStyle(this._elem).paddingBottom);
    height += parseFloat(getComputedStyle(this._elem).paddingTop);

    let activeSlide = this.getActiveSlideElem(slider);
    if (!activeSlide) return;

    height += activeSlide.scrollHeight;

    this._elem.style.height = `${height}px`;

    if (!this._elem.classList.contains('fade-slider')) {
      this._elem.classList.add('fade-slider');
    }
  }

  alignArrows() {
    if (!this._options.alignArrowsTo || !this._navContainer) return false;
    let elem = this._elem.querySelector(this._options.alignArrowsTo);
    this._navContainer.style.top = `${elem.clientHeight / 2}px`;
  }

  updateClasses(slider) {
    if (!slider) return false;

    let slide = slider.track.details.rel;
    let maxSlides = slider.track.details.slides.length;
    let perView = this._slidesPerView;
    let endSlide = slide + perView;

    let canSlide = perView !== maxSlides;

    if (this._prevArrow && !this._options.loop) {
      if (slide === 0 || !canSlide) {
        this._prevArrow.setAttribute('disabled', 'disabled');
      } else {
        this._prevArrow.removeAttribute('disabled');
      }
    }

    if (this._nextArrow && !this._options.loop) {
      if (this.isAtEnd(slider) || !canSlide) {
        this._nextArrow.setAttribute('disabled', 'disabled');
      } else {
        this._nextArrow.removeAttribute('disabled');
      }
    }

    if (this._dots) {
      let dotIndex = slide;

      for (let dot of this._dots) {
        dot.classList.remove('active');

        if (Number(dot.dataset.dot) === dotIndex) {
          dot.classList.add('active');
        }
      }
    }

    let activeSlide = this.getActiveSlideElem(slider);

    for (let slide of this._slides) {
      slide.classList.remove('active');
    }

    if (activeSlide) {
      activeSlide.classList.add('active');
    }

    let idx = slider.track.details.maxIdx;
    let totalSlides = idx === Infinity ? maxSlides : idx + 1;

    this._elem.style.setProperty('--current-slide', slide + 1);
    this._elem.style.setProperty('--total-slides', totalSlides);

    if (this._options.progressSelector) {
      let progressElem = document.querySelector(this._options.progressSelector);
      progressElem.style.setProperty('--current-slide', slide + 1);
      progressElem.style.setProperty('--total-slides', totalSlides);

      if (totalSlides === 1) {
        progressElem.classList.add('hide');
      }
    }
  }

  isAtEnd(slider = this._slider) {
    let slide = slider.track.details.rel;
    let idx = slider.track.details.maxIdx;
    let maxSlides = slider.track.details.slides.length;
    let totalSlides = idx === Infinity ? maxSlides : idx + 1;

    return slide + 1 >= totalSlides;
  }

  refresh() {
    return this._slider.refresh();
  }

  resize() {
    return this._slider.resize();
  }

  update() {
    this.unmount();
    this._options = this._optionsCopy;
    this.mount();
  }
}
